// Indian-context currency + number formatting helpers

export const ASSET_CLASS_COLORS = {
  "Equity": "#123A63",
  "Mutual Funds": "#0E7A5F",
  "Fixed Income/Bonds": "#B08D57",
  "Real Estate": "#7C3E2B",
  "Private Equity/VC": "#1F2937",
  "Gold/Commodities": "#A16207",
  "Cash": "#0F766E",
  "Offshore/Foreign": "#1D4ED8",
  "Alternatives": "#6D28D9",
};

export const CHART_PALETTE = [
  "#0F3D2E", "#123A63", "#B08D57", "#7C3E2B", "#0E7A5F",
  "#A16207", "#1D4ED8", "#6D28D9", "#0F766E", "#B45309",
];

export const CURRENCY_SYMBOLS = {
  INR: "\u20B9", USD: "$", EUR: "\u20AC", GBP: "\u00A3",
  AED: "AED ", SGD: "S$", CHF: "CHF ", JPY: "\u00A5", AUD: "A$",
};

export function fmtINR(value, { compact = false, decimals = 0 } = {}) {
  const v = Number(value || 0);
  if (compact) return "\u20B9" + compactIndian(v);
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: decimals,
  }).format(v);
}

// Lakh / Crore compaction for headline figures
export function compactIndian(value) {
  const v = Math.abs(Number(value || 0));
  const sign = value < 0 ? "-" : "";
  if (v >= 1.0e7) return sign + (v / 1.0e7).toFixed(2) + " Cr";
  if (v >= 1.0e5) return sign + (v / 1.0e5).toFixed(2) + " L";
  if (v >= 1.0e3) return sign + (v / 1.0e3).toFixed(1) + " K";
  return sign + v.toFixed(0);
}

export function fmtCurrency(value, ccy = "INR", { decimals = 0 } = {}) {
  const sym = CURRENCY_SYMBOLS[ccy] || ccy + " ";
  const num = new Intl.NumberFormat("en-IN", { maximumFractionDigits: decimals }).format(Number(value || 0));
  return sym + num;
}

export function fmtNum(value, decimals = 0) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: decimals }).format(Number(value || 0));
}

export function fmtPct(value, decimals = 1) {
  const v = Number(value || 0);
  return (v >= 0 ? "+" : "") + v.toFixed(decimals) + "%";
}

export function fmtDate(iso) {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export const ASSET_CLASSES = [
  "Equity", "Mutual Funds", "Fixed Income/Bonds", "Real Estate",
  "Private Equity/VC", "Gold/Commodities", "Cash", "Offshore/Foreign", "Alternatives",
];
export const ENTITY_TYPES = [
  "Operating Company (OpCo)", "Holding Company (HoldCo)", "Private Limited",
  "Public Limited", "NBFC", "Revocable Trust", "Irrevocable Trust", "LLP",
  "Investment Vehicle", "SPV", "Individual", "HUF", "Partnership",
];

// Entity types that carry an MCA / corporate profile
export const CORPORATE_TYPES = [
  "Private Limited", "Public Limited", "Holding Company (HoldCo)",
  "Operating Company (OpCo)", "NBFC", "LLP",
];
export const isCorporateType = (t) => CORPORATE_TYPES.includes(t);

export const COMPANY_CLASSES = ["Private", "Public", "One Person Company"];
export const COMPANY_CATEGORIES = [
  "Company limited by shares", "Company limited by guarantee", "Unlimited company",
];
export const COMPANY_SUBCATEGORIES = [
  "Non-government company", "Government company",
  "Subsidiary of foreign company", "State government company",
];
export const COMPANY_STATUSES = [
  "Active", "Dormant", "Under Process of Striking Off", "Strike Off",
  "Under Liquidation", "Amalgamated", "Converted to LLP",
];
export const ROC_CODES = [
  "RoC-Mumbai", "RoC-Delhi", "RoC-Bangalore", "RoC-Chennai", "RoC-Kolkata",
  "RoC-Ahmedabad", "RoC-Hyderabad", "RoC-Pune", "RoC-Jaipur", "RoC-Chandigarh",
  "RoC-Coimbatore", "RoC-Ernakulam",
];
export const DIRECTOR_DESIGNATIONS = [
  "Managing Director", "Whole-time Director", "Director",
  "Independent Director", "Nominee Director", "Additional Director",
];
export const TXN_TYPES = [
  { value: "capital_infusion", label: "Capital Infusion" },
  { value: "investment", label: "Investment" },
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
  { value: "inter_entity_transfer", label: "Inter-entity Transfer" },
  { value: "dividend", label: "Dividend" },
  { value: "loan_given", label: "Loan Given" },
  { value: "loan_received", label: "Loan Received" },
];
export const txnTypeLabel = (v) => (TXN_TYPES.find((t) => t.value === v) || {}).label || v;
export const PE_STRATEGIES = ["Private Equity", "Venture Capital", "Private Credit", "Real Assets", "Fund of Funds", "Secondaries"];
export const DAY_COUNTS = ["30/360", "ACT/365", "ACT/ACT"];
export const TASK_TYPES = ["Tax", "Filing", "KYC", "Audit", "Renewal", "Other"];
export const DOC_TYPES = ["Agreement", "Statement", "KYC", "Tax", "Trust Deed", "Report", "Other"];
export const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD", "CHF", "JPY", "AUD"];
export const JURISDICTIONS = [
  { code: "IN", name: "India" }, { code: "US", name: "United States" },
  { code: "UK", name: "United Kingdom" }, { code: "AE", name: "UAE" },
  { code: "SG", name: "Singapore" },
];

export function fmtMultiple(v) { return Number(v || 0).toFixed(2) + "x"; }
